import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import BlogPostCard from '@/components/BlogPostCard'
import BlogPostListPreview from '@/components/blog/BlogPostListPreview'
import EventPagination from '@/components/EventPagination'
import type {
  BlogPostCard as BlogPostCardType,
  BlogCategorySummary,
  PaginationMeta,
  SharedProps,
} from '@/types'

type Props = {
  posts: BlogPostCardType[]
  featured_post: BlogPostCardType | null
  categories: BlogCategorySummary[]
  current_category: BlogCategorySummary | null
  filters: { q?: string }
  pagination: PaginationMeta
}

export default function BlogIndex({
  posts,
  featured_post: featuredPost,
  categories,
  current_category: currentCategory,
  filters,
  pagination,
}: Props) {
  const { app_url } = usePage<SharedProps>().props
  const [query, setQuery] = useState(filters.q || '')

  const baseUrl = currentCategory ? `/blog/category/${currentCategory.slug}` : '/blog'
  const filterState = { q: filters.q }
  const remaining = featuredPost ? posts.filter((p) => p.id !== featuredPost.id) : posts

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    const params: Record<string, string> = {}
    if (query.trim()) params.q = query.trim()
    router.get(baseUrl, params, { preserveState: false })
  }

  return (
    <>
      <Head title="Run of Show | Gala Season">
        <meta head-key="description" name="description" content="Stories, ideas, and reporting from the world of charitable galas and fundraising — Run of Show, the Gala Season blog." />
        <meta head-key="og:title" property="og:title" content="Run of Show — Gala Season" />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/blog`} />
        <meta head-key="og:description" property="og:description" content="Stories, ideas, and reporting from the world of charitable galas and fundraising." />
      </Head>

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute inset-x-0 top-0 h-[140%] bg-[linear-gradient(to_bottom,oklch(0.85_0.11_62/0.55)_0%,oklch(0.91_0.07_72/0.28)_28%,transparent_62%)]" />
          <div className="absolute -top-1/4 -right-[8%] h-[95%] w-[60%] rounded-full bg-[radial-gradient(circle,oklch(0.80_0.15_55/0.45)_0%,transparent_65%)] blur-2xl" />
        </div>
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
              <span aria-hidden="true">✦</span>
              Run of Show
            </p>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              {currentCategory ? (
                currentCategory.name
              ) : (
                <>
                  The{' '}
                  <span className="font-serif italic text-primary underline decoration-primary/30 decoration-4 underline-offset-4">
                    Run of Show
                  </span>
                  .
                </>
              )}
            </h1>
            <p className="mt-6 max-w-[48ch] text-lg text-foreground text-pretty">
              {currentCategory?.description ||
                'Reporting, data, interviews, and ideas for people who plan, host, and fund charitable events.'}
            </p>

            <form onSubmit={handleSearch} className="mt-9 flex max-w-xl gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search articles…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit">Search</Button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Link
                href="/blog"
                className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                  !currentCategory
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-black/10 text-foreground hover:bg-muted'
                }`}
              >
                All
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.id}
                  href={`/blog/category/${c.slug}`}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                    currentCategory?.id === c.id
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-black/10 text-foreground hover:bg-muted'
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
          {featuredPost ? (
            <div className="lg:pl-8">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">Featured</p>
              <BlogPostCard post={featuredPost} />
            </div>
          ) : posts.length > 0 && (
            <div className="lg:pl-8">
              <BlogPostListPreview
                posts={posts}
                title={currentCategory || filters.q ? 'Matching articles' : 'Latest articles'}
              />
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        {posts.length === 0 ? (
          <EmptyState query={filters.q} />
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {remaining.map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>

            <EventPagination pagination={pagination} baseUrl={baseUrl} filters={filterState} />
          </>
        )}
      </section>
    </>
  )
}

function EmptyState({ query }: { query?: string }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl bg-muted/30 p-12 text-center ring-1 ring-black/5">
      <h2 className="font-serif text-2xl font-semibold tracking-tight">
        {query ? 'No matching articles.' : 'No articles yet.'}
      </h2>
      <p className="mt-2 text-base text-muted-foreground">
        {query
          ? 'Try a different search term, or browse all articles.'
          : 'Check back soon — the first stories are on their way.'}
      </p>
      {query && (
        <Link
          href="/blog"
          className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
        >
          Clear search
        </Link>
      )}
    </div>
  )
}
